import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const EditStaff = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    services: [],
    permissions: {}
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStaffData();
  }, [id]);

  const fetchStaffData = async () => {
    try {
      const response = await apiService.get(`editStaff/${id}`);
      const data = response.data.data;
      
      const normalizedPermissions = {};
      if (data.permissions && Array.isArray(data.permissions)) {
        data.permissions.forEach(permission => {
          normalizedPermissions[permission.page] = {};
          permission.modules.forEach(module => {
            normalizedPermissions[permission.page][module.name] = module.actions || [];
          });
        });
      }

      setFormData({
        id: data._id,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        password: '',
        services: data.services || [],
        permissions: normalizedPermissions
      });
    } catch (error) {
      toast.error('Failed to fetch staff data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiService.post('updateStaff', formData);
      if (response.status) {
        toast.success('Staff updated successfully');
        navigate('/staff');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handlePermissionChange = (page, module, action) => {
    setFormData(prev => {
      const newPerms = JSON.parse(JSON.stringify(prev.permissions));
      if (!newPerms[page]) newPerms[page] = {};
      if (!newPerms[page][module]) newPerms[page][module] = [];
      
      if (newPerms[page][module].includes(action)) {
        newPerms[page][module] = newPerms[page][module].filter(a => a !== action);
      } else {
        newPerms[page][module] = [...newPerms[page][module], action];
      }
      return { ...prev, permissions: newPerms };
    });
  };

  const isChecked = (page, module, action) => {
    return formData.permissions[page]?.[module]?.includes(action) || false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 border-4 border-[#009ef7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-6">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to="/staff" className="text-gray-500 hover:text-gray-700">Staff</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Edit Staff</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm max-w-4xl">
        <div className="p-4 sm:p-6 border-b">
          <h2 className="text-lg sm:text-xl font-semibold flex items-center">
            <i className="bi bi-person-badge mr-2"></i>Edit Staff
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">First Name <span className="text-red-500">*</span></label>
              <input type="text" required value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7]" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Last Name</label>
              <input type="text" value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7]" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email <span className="text-red-500">*</span></label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7]" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder="Enter to change" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7]" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <i className={`bi bi-eye${showPassword ? '' : '-slash'} text-gray-500`}></i>
                </button>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">Menus Access</label>
            <div className="flex flex-wrap gap-4">
              {user?.role === 'admin' && (
                <>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('users')} onChange={() => handleServiceChange('users')} className="mr-2" />Users</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('vendors')} onChange={() => handleServiceChange('vendors')} className="mr-2" />Vendors</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('banners')} onChange={() => handleServiceChange('banners')} className="mr-2" />Banners</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('locations')} onChange={() => handleServiceChange('locations')} className="mr-2" />Locations</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('blog')} onChange={() => handleServiceChange('blog')} className="mr-2" />Blog</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('ticket')} onChange={() => handleServiceChange('ticket')} className="mr-2" />Ticket</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('suggestion')} onChange={() => handleServiceChange('suggestion')} className="mr-2" />Suggestion</label>
                </>
              )}
              <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('leads')} onChange={() => handleServiceChange('leads')} className="mr-2" />Leads</label>
              {user?.services?.includes('packages') && <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('packages')} onChange={() => handleServiceChange('packages')} className="mr-2" />Packages</label>}
              {user?.services?.includes('hotels') && <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('hotels')} onChange={() => handleServiceChange('hotels')} className="mr-2" />Hotels</label>}
              {user?.services?.includes('guide') && <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('guide')} onChange={() => handleServiceChange('guide')} className="mr-2" />Guide</label>}
              {user?.services?.includes('vehicles') && <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('vehicles')} onChange={() => handleServiceChange('vehicles')} className="mr-2" />Vehicles</label>}
              <label className="flex items-center"><input type="checkbox" checked={formData.services.includes('faq')} onChange={() => handleServiceChange('faq')} className="mr-2" />FAQ</label>
            </div>
          </div>

          {user?.role === 'admin' && (
            <>
              <div className="bg-white border rounded-lg mb-4">
                <div className="bg-gray-50 px-4 py-3 border-b"><h5 className="font-semibold text-sm sm:text-base">Dashboard Permissions</h5></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-gray-50"><tr><th className="px-2 sm:px-2 sm:px-4 py-2 text-left">Module</th><th className="px-2 sm:px-2 sm:px-4 py-2 text-center">View</th></tr></thead>
                    <tbody>
                      {['dashboard', 'user_overview', 'platform_overview', 'order_overview', 'feedback_overview', 'travel_services', 'lead_graph', 'user_vendor_graph', 'user_graph', 'order_graph', 'recent_vendor', 'unlock_graph', 'source_graph', 'call_back'].map(mod => (
                        <tr key={mod} className="border-t"><td className="px-2 sm:px-2 sm:px-4 py-2">{mod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</td><td className="px-2 sm:px-2 sm:px-4 py-2 text-center"><input type="checkbox" checked={isChecked('dashboard', mod, 'view')} onChange={() => handlePermissionChange('dashboard', mod, 'view')} /></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border rounded-lg mb-4">
                <div className="bg-gray-50 px-4 py-3 border-b"><h5 className="font-semibold text-sm sm:text-base">Admin Staff Permissions</h5></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-gray-50"><tr><th className="px-2 sm:px-2 sm:px-4 py-2 text-left">Module</th><th className="px-2 sm:px-2 sm:px-4 py-2 text-center">View</th><th className="px-2 sm:px-2 sm:px-4 py-2 text-center">Add</th><th className="px-2 sm:px-2 sm:px-4 py-2 text-center">Edit</th><th className="px-2 sm:px-2 sm:px-4 py-2 text-center">Delete</th></tr></thead>
                    <tbody>
                      {[
                        {page: 'admin_page', modules: ['admin_page', 'user', 'vendor', 'vendor_verification']},
                        {page: 'location_page', modules: ['location_page:view', 'location', 'country', 'state', 'city']},
                        {page: 'package_page', modules: ['package_page:view', 'package', 'package_facility', 'package_type', 'package_category']},
                        {page: 'blog', modules: ['blog']},
                        {page: 'contactus', modules: ['contactus']},
                        {page: 'caller_page', modules: ['caller_page:view', 'vendor:view', 'travel_post', 'pending_transactions:view']},
                        {page: 'manage_travel_post', modules: ['manage_travel_post:view', 'travel_post', 'offer_list', 'unlock_list', 'max_offer', 'max_unlocked', 'replacement_list', 'lead_analytics']},
                        {page: 'itsupport', modules: ['itsupport']},
                        {page: 'email', modules: ['email']},
                        {page: 'notification', modules: ['notification']},
                        {page: 'banner', modules: ['banner']},
                        {page: 'category', modules: ['category']},
                        {page: 'faq', modules: ['faq']},
                        {page: 'web_settings', modules: ['web_settings:view', 'offers', 'whatsnew', 'common_settings', 'static_page_meta_data']}
                      ].flatMap(({page, modules}) => modules.map(mod => {
                        const [modName, onlyAction] = mod.split(':');
                        const actions = onlyAction ? [onlyAction] : ['view', 'add', 'edit', 'delete'];
                        return (
                          <tr key={`${page}-${modName}`} className="border-t">
                            <td className="px-2 sm:px-2 sm:px-4 py-2">{modName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</td>
                            {['view', 'add', 'edit', 'delete'].map(action => (
                              <td key={action} className="px-2 sm:px-2 sm:px-4 py-2 text-center">
                                {actions.includes(action) ? <input type="checkbox" checked={isChecked(page, modName, action)} onChange={() => handlePermissionChange(page, modName, action)} /> : '--'}
                              </td>
                            ))}
                          </tr>
                        );
                      }))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
            <Link to="/staff" className="px-2 sm:px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-center">Cancel</Link>
            <button type="submit" disabled={submitting} className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50">{submitting ? 'Updating...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStaff;
